import type {
  ChemistryBlock,
  ExperimentRecord,
  RelatedLiteratureSummary,
  RelatedReservationSummary,
  ReagentRow
} from '../../services/experimentRecords/experimentRecordService';

export function coerceReagentRows(c: Partial<ChemistryBlock> | undefined): ReagentRow[] {
  const ch = c || {};
  if (Array.isArray(ch.reagentRows) && ch.reagentRows.length > 0) {
    return ch.reagentRows.map((r) => ({
      compound: String(r.compound || ''),
      molarity: String(r.molarity || ''),
      volume: String(r.volume || '')
    }));
  }
  const leg = String(ch.reagentsAndSolvents || '').trim();
  if (leg) {
    const lines = leg.split(/\n/).filter((l) => l.trim());
    const rows = lines.map((line) => {
      const parts = line.split(/\t/).map((p) => p.trim());
      if (parts.length >= 3) {
        return { compound: parts[0] || '', molarity: parts[1] || '', volume: parts[2] || '' };
      }
      return { compound: line.trim(), molarity: '', volume: '' };
    });
    return rows.length ? rows : [{ compound: '', molarity: '', volume: '' }];
  }
  return [{ compound: '', molarity: '', volume: '' }];
}

export function formatReservationSlot(r: RelatedReservationSummary): string {
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  const s = r.startTime ? new Date(r.startTime).toLocaleTimeString('zh-CN', opts) : '';
  const e = r.endTime ? new Date(r.endTime).toLocaleTimeString('zh-CN', opts) : '';
  if (s && e) return `时段：${s} – ${e}`;
  if (s) return `时段：${s}`;
  return '';
}

/** 新建表单时 rec 为 null，允许编辑 */
export function canEditRecord(rec: ExperimentRecord | null, userId: string | undefined, role: string | null): boolean {
  if (!rec) return true;
  if (!userId) return false;
  const cid = rec.createdBy && (rec.createdBy as { _id?: string })._id;
  if (cid && String(cid) === String(userId)) return true;
  if (role === '管理员' || role === '超级管理员') return true;
  return false;
}

export function normalizeRelatedResFromRecord(rec: ExperimentRecord): RelatedReservationSummary[] {
  const raw = rec.relatedReservations;
  if (!Array.isArray(raw)) return [];
  const out: RelatedReservationSummary[] = [];
  for (const x of raw) {
    if (typeof x === 'string') out.push({ _id: x, purpose: '（预约）' });
    else if (x && typeof x === 'object' && '_id' in x) {
      const o = { ...(x as RelatedReservationSummary) };
      o._id = String(o._id);
      out.push(o);
    }
  }
  return out;
}

export function instrumentNameFromReservation(r: RelatedReservationSummary): string {
  const ins = r.instrumentId;
  if (ins && typeof ins === 'object' && 'name' in ins) {
    return String((ins as { name?: string }).name || '').trim() || '仪器';
  }
  return '仪器';
}

export function formatReservationChipSubtitle(r: RelatedReservationSummary): string {
  const ins = instrumentNameFromReservation(r);
  const st = r.startTime
    ? new Date(r.startTime).toLocaleString('zh-CN', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';
  const stat = r.status ? String(r.status) : '';
  return [ins, st, stat].filter(Boolean).join(' · ');
}

export function normalizeRelatedLitFromRecord(rec: ExperimentRecord): RelatedLiteratureSummary[] {
  const raw = rec.relatedLiteratures;
  if (!Array.isArray(raw)) return [];
  const out: RelatedLiteratureSummary[] = [];
  for (const x of raw) {
    if (typeof x === 'string') out.push({ _id: x, title: '（文献）' });
    else if (x && typeof x === 'object' && '_id' in x) {
      const o = x as RelatedLiteratureSummary;
      out.push({
        _id: String(o._id),
        title: o.title || '无标题',
        authors: o.authors,
        year: o.year,
        journal: o.journal,
        doi: o.doi
      });
    }
  }
  return out;
}
